import * as React from "react";
import {
  AlertCircle,
  Info,
  LayoutGrid,
  Loader2,
  Plus,
  RotateCcw,
  RotateCw,
  Save,
  Sparkles,
  SquarePlus,
  Trash2,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import type { ApiError } from "@eduviaos/shared";
import { FloorBackdrop } from "@/components/seating/FloorCanvas";
import { ResetFloorPlanDialog } from "@/components/seating/ResetFloorPlanDialog";
import { seatTransform } from "@/lib/seating/floorStyles";
import { useCanEditSeating } from "@/hooks/useSeatingAuthoring";
import {
  DESK_H,
  DESK_W,
  FALLBACK_REASON_COPY,
  clamp,
  findFreeSpot,
  invalidSeatIndexes,
  nextSeatNumber,
  snap,
  useFloorPlan,
  useGenerateFloorPlan,
  useSaveFloorPlan,
  type FloorPlanBody,
  type FloorPlanDraftResponse,
  type FloorPlanResponse,
  type FloorSeatPlacement,
  type FloorZoneShape,
} from "@/lib/seating/floorPlan";

/**
 * Phase SC-22c — floor plan editor (feature 4.69).
 *
 * The admin describes the rooms, asks the server for a draft (AI when
 * available, a deterministic grid otherwise), then edits it: drag seats and
 * rooms with grid snap, rotate, add or remove, and save. The server
 * re-validates the whole layout; the client check only keeps the Save
 * button honest. Nothing is saved until Save, and a save carries the
 * version that was loaded so two admins cannot overwrite each other.
 */

type Selection = { kind: "seat"; index: number } | { kind: "zone"; index: number } | null;

interface RoomSpecRow {
  key: number;
  name: string;
  seats: string;
}

type DragState = {
  kind: "seat" | "zone" | "resize";
  index: number;
  start: { x: number; y: number };
  origin: { x: number; y: number; w: number; h: number };
  seatOrigins: Array<{ index: number; x: number; y: number }>;
};

const BLANK_FLOOR: FloorPlanBody = {
  branch_id: null,
  name: "Main floor",
  width: 300,
  height: 200,
  grid_size: 1,
  zones: [],
  features: [{ kind: "entrance", label: "Entrance", rotation: 0, x: 0, y: 92, w: 4, h: 16 }],
  seats: [],
};

function toBody(plan: FloorPlanResponse | FloorPlanBody): FloorPlanBody {
  return {
    branch_id: plan.branch_id,
    name: plan.name,
    width: plan.width,
    height: plan.height,
    grid_size: plan.grid_size,
    zones: plan.zones,
    features: plan.features,
    seats: plan.seats,
  };
}

function metres(units: number): string {
  const m = units / 10;
  return Number.isInteger(m) ? String(m) : m.toFixed(1);
}

export function FloorPlanPage() {
  const { canEditSeating, missingRbac } = useCanEditSeating();
  const planQuery = useFloorPlan(null);

  return (
    <div className="space-y-6 pb-24">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Floor plan</PageHeaderHeading>
            <PageHeaderDescription>
              Draw your rooms and place every seat. Learners see this map when they book.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac ? (
        <Card data-testid="seating-missing-rbac-notice" className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">View-only access</CardTitle>
            <CardDescription>
              You don&apos;t have the <span className="font-medium">Manage zones</span> permission,
              so the floor plan can&apos;t be edited. Ask an administrator to grant it.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : planQuery.isLoading ? (
        <div
          className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]"
          aria-busy="true"
          aria-label="Loading floor plan"
        >
          <div className="bg-muted/60 aspect-[3/2] w-full animate-pulse rounded-lg motion-reduce:animate-none" />
          <div className="bg-muted/60 h-64 w-full animate-pulse rounded-lg motion-reduce:animate-none" />
        </div>
      ) : planQuery.isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <span className="flex-1">Couldn&apos;t load the floor plan.</span>
          <Button variant="ghost" size="sm" onClick={() => void planQuery.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <FloorPlanEditor
          saved={planQuery.data ?? null}
          canEdit={canEditSeating}
          reload={async () => (await planQuery.refetch()).data ?? null}
        />
      )}
    </div>
  );
}

function FloorPlanEditor({
  saved,
  canEdit,
  reload,
}: {
  saved: FloorPlanResponse | null;
  canEdit: boolean;
  reload: () => Promise<FloorPlanResponse | null>;
}) {
  const generate = useGenerateFloorPlan();
  const save = useSaveFloorPlan();

  const [draft, setDraft] = React.useState<FloorPlanBody | null>(saved ? toBody(saved) : null);
  const [dirty, setDirty] = React.useState(false);
  const [selection, setSelection] = React.useState<Selection>(null);
  const [draftInfo, setDraftInfo] = React.useState<FloorPlanDraftResponse | null>(null);
  const [serverWarnings, setServerWarnings] = React.useState<string[]>(saved?.warnings ?? []);
  const [stale, setStale] = React.useState(false);
  const [resetOpen, setResetOpen] = React.useState(false);

  const invalid = React.useMemo(
    () => (draft ? invalidSeatIndexes(draft) : new Set<number>()),
    [draft],
  );

  const update = React.useCallback((mutate: (plan: FloorPlanBody) => FloorPlanBody) => {
    setDraft((prev) => (prev ? mutate(prev) : prev));
    setDirty(true);
  }, []);

  function onGenerated(result: FloorPlanDraftResponse) {
    setDraft(result.plan);
    setDraftInfo(result);
    setDirty(true);
    setSelection(null);
    setServerWarnings([]);
    toast.success(
      result.source === "ai"
        ? `Layout drafted with ${result.plan.seats.length} seats. Review it, then save.`
        : `Grid layout drafted with ${result.plan.seats.length} seats. Review it, then save.`,
    );
  }

  function onSave() {
    if (!draft) return;
    const needsCreate = draft.seats.some((s) => !s.seat_id) || draft.zones.some((z) => !z.zone_id);
    save.mutate(
      {
        ...draft,
        create_missing_seats: needsCreate,
        expected_version: saved?.version ?? null,
      },
      {
        onSuccess: (result) => {
          setDraft(toBody(result));
          setDirty(false);
          setDraftInfo(null);
          setServerWarnings(result.warnings);
          toast.success("Floor plan saved");
        },
        onError: (err: ApiError) => {
          if (err.details?.code === "seat.floor_plan_stale") {
            setStale(true);
            return;
          }
          toast.error(err.message || "Couldn't save the floor plan");
        },
      },
    );
  }

  function onDiscard() {
    setDraft(saved ? toBody(saved) : null);
    setDirty(false);
    setDraftInfo(null);
    setSelection(null);
  }

  function onResetDone() {
    setDraft(null);
    setDirty(false);
    setDraftInfo(null);
    setSelection(null);
    setServerWarnings([]);
  }

  async function onReload() {
    const latest = await reload();
    setDraft(latest ? toBody(latest) : null);
    setServerWarnings(latest?.warnings ?? []);
    setDirty(false);
    setDraftInfo(null);
    setSelection(null);
    setStale(false);
  }

  return (
    <div className="space-y-6">
      {canEdit ? (
        <GeneratePanel
          hasDraft={draft !== null}
          pending={generate.isPending}
          onGenerate={(input) =>
            generate.mutate(input, {
              onSuccess: onGenerated,
              onError: (err) => toast.error(err.message || "Couldn't draft a layout"),
            })
          }
        />
      ) : null}

      {stale ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-500/5 px-3 py-2 text-sm"
        >
          <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <span className="flex-1">
            Someone else saved this floor plan while you were editing. Reload to see their version;
            your unsaved changes will be lost.
          </span>
          <Button size="sm" variant="outline" onClick={() => void onReload()}>
            Reload
          </Button>
        </div>
      ) : null}

      {draftInfo ? <DraftNotice info={draftInfo} onDismiss={() => setDraftInfo(null)} /> : null}

      {serverWarnings.length > 0 && !draftInfo ? (
        <ul className="text-muted-foreground list-disc space-y-0.5 pl-5 text-xs">
          {serverWarnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}

      {saved && canEdit ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-muted-foreground text-xs">
            Saved version {saved.version} · {saved.seats.length} seat
            {saved.seats.length === 1 ? "" : "s"} placed
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setResetOpen(true)}
            disabled={save.isPending}
            data-testid="floor-plan-reset"
          >
            <RotateCcw aria-hidden="true" className="size-4" />
            Reset floor plan
          </Button>
          <ResetFloorPlanDialog
            open={resetOpen}
            onOpenChange={setResetOpen}
            branchId={saved.branch_id}
            version={saved.version}
            seatCount={saved.seats.length}
            onReset={onResetDone}
            onStale={() => setStale(true)}
          />
        </div>
      ) : null}

      {draft ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
          <Card className="overflow-hidden">
            <EditorToolbar
              canEdit={canEdit}
              selection={selection}
              onAddRoom={() => {
                update((plan) => addRoom(plan));
                setSelection({ kind: "zone", index: draft.zones.length });
              }}
              onAddSeat={() => {
                const zoneIndex = selectedZoneIndex(draft, selection);
                update((plan) => addSeat(plan, zoneIndex));
                setSelection({ kind: "seat", index: draft.seats.length });
              }}
              onRotate={() => {
                if (selection?.kind !== "seat") return;
                update((plan) => rotateSeat(plan, selection.index));
              }}
              onDelete={() => {
                if (!selection) return;
                update((plan) => removeItem(plan, selection));
                setSelection(null);
              }}
            />
            <EditorCanvas
              plan={draft}
              canEdit={canEdit}
              invalid={invalid}
              selection={selection}
              onSelect={setSelection}
              onChange={update}
            />
          </Card>
          <Inspector plan={draft} selection={selection} canEdit={canEdit} onChange={update} />
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <span className="bg-muted text-muted-foreground grid size-12 place-items-center rounded-xl">
              <LayoutGrid aria-hidden="true" className="size-6" />
            </span>
            <div className="space-y-1">
              <p className="font-medium">No floor plan yet</p>
              <p className="text-muted-foreground max-w-sm text-sm">
                Describe your rooms above and generate a layout, or start from an empty floor.
              </p>
            </div>
            {canEdit ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDraft(BLANK_FLOOR);
                  setDirty(true);
                }}
              >
                Start with an empty floor
              </Button>
            ) : null}
          </CardContent>
        </Card>
      )}

      {draft && dirty && canEdit ? (
        <div className="border-border/60 bg-card/95 sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3 shadow-sm backdrop-blur">
          <span className="text-muted-foreground text-sm">
            {invalid.size > 0
              ? `${invalid.size} seat${invalid.size === 1 ? "" : "s"} overlap or sit outside the floor. Move them before saving.`
              : "You have unsaved changes."}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onDiscard} disabled={save.isPending}>
              Discard
            </Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={save.isPending || invalid.size > 0}
              data-testid="floor-plan-save"
            >
              {save.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : (
                <Save aria-hidden="true" className="size-4" />
              )}
              {save.isPending ? "Saving…" : "Save floor plan"}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Generate panel ────────────────────────────────────────────────────

let rowKey = 0;
function newRow(): RoomSpecRow {
  rowKey += 1;
  return { key: rowKey, name: "", seats: "" };
}

function GeneratePanel({
  hasDraft,
  pending,
  onGenerate,
}: {
  hasDraft: boolean;
  pending: boolean;
  onGenerate: (input: {
    zones: Array<{ name: string; seat_count: number }>;
    notes: string | null;
    width: number | null;
    height: number | null;
  }) => void;
}) {
  const [rows, setRows] = React.useState<RoomSpecRow[]>(() => [newRow()]);
  const [notes, setNotes] = React.useState("");
  const [widthM, setWidthM] = React.useState("");
  const [heightM, setHeightM] = React.useState("");

  const specs = rows
    .map((r) => ({ name: r.name.trim(), seat_count: Number.parseInt(r.seats, 10) }))
    .filter((r) => r.name && Number.isFinite(r.seat_count) && r.seat_count > 0);
  const partial = rows.some(
    (r) => (r.name.trim() !== "") !== (r.seats.trim() !== "") && (r.name.trim() || r.seats.trim()),
  );

  function metresToUnits(raw: string): number | null {
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 10) : null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Describe the floor</CardTitle>
        <CardDescription>
          List each room and how many seats it holds. Leave the rooms empty to lay out the rooms and
          seats you already have.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={row.key} className="grid grid-cols-[minmax(0,1fr)_6.5rem_auto] gap-2">
              <Input
                aria-label={`Room ${i + 1} name`}
                placeholder="Quiet hall"
                value={row.name}
                maxLength={120}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r) => (r.key === row.key ? { ...r, name: e.target.value } : r)),
                  )
                }
              />
              <Input
                aria-label={`Room ${i + 1} seats`}
                placeholder="Seats"
                inputMode="numeric"
                value={row.seats}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r) =>
                      r.key === row.key ? { ...r, seats: e.target.value.replace(/\D/g, "") } : r,
                    ),
                  )
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove room ${i + 1}`}
                disabled={rows.length === 1}
                onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
              >
                <X aria-hidden="true" className="size-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRows((prev) => [...prev, newRow()])}
            disabled={rows.length >= 30}
          >
            <Plus aria-hidden="true" className="size-4" />
            Add room
          </Button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="floor-width">Floor width (m)</Label>
            <Input
              id="floor-width"
              inputMode="decimal"
              placeholder="Auto"
              value={widthM}
              onChange={(e) => setWidthM(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="floor-height">Floor depth (m)</Label>
            <Input
              id="floor-height"
              inputMode="decimal"
              placeholder="Auto"
              value={heightM}
              onChange={(e) => setHeightM(e.target.value)}
            />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="floor-notes">Notes</Label>
          <Textarea
            id="floor-notes"
            rows={3}
            maxLength={2000}
            placeholder="Door on the east wall, windows facing south, washroom near the entrance."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() =>
              onGenerate({
                zones: specs,
                notes: notes.trim() || null,
                width: metresToUnits(widthM),
                height: metresToUnits(heightM),
              })
            }
            disabled={pending || partial}
            data-testid="floor-plan-generate"
          >
            {pending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : (
              <Sparkles aria-hidden="true" className="size-4" />
            )}
            {pending ? "Drafting layout…" : "Generate layout with AI"}
          </Button>
          <span className="text-muted-foreground text-xs">
            {partial
              ? "Give every room a name and a seat count."
              : hasDraft
                ? "Replaces the layout on screen. Nothing is saved until you press Save."
                : "Nothing is saved until you press Save."}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function DraftNotice({ info, onDismiss }: { info: FloorPlanDraftResponse; onDismiss: () => void }) {
  const fallback = info.source === "fallback";
  return (
    <div
      role="status"
      data-testid="floor-plan-draft-notice"
      className={cn(
        "flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
        fallback ? "border-amber-300/60 bg-amber-500/5" : "border-primary/30 bg-primary/5",
      )}
    >
      {fallback ? (
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-amber-600" />
      ) : (
        <Sparkles aria-hidden="true" className="text-primary mt-0.5 size-4 shrink-0" />
      )}
      <div className="flex-1 space-y-1">
        <p>
          {fallback
            ? (FALLBACK_REASON_COPY[info.fallback_reason ?? ""] ?? "This is a grid layout.")
            : "Drafted with AI."}{" "}
          {info.new_seat_count > 0
            ? `${info.new_seat_count} new seat${info.new_seat_count === 1 ? "" : "s"} will be created when you save.`
            : null}
        </p>
        {info.warnings.length > 0 ? (
          <ul className="text-muted-foreground list-disc pl-5 text-xs">
            {info.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label="Dismiss"
        onClick={onDismiss}
      >
        <X aria-hidden="true" className="size-4" />
      </Button>
    </div>
  );
}

// ── Toolbar + canvas ──────────────────────────────────────────────────

function EditorToolbar({
  canEdit,
  selection,
  onAddRoom,
  onAddSeat,
  onRotate,
  onDelete,
}: {
  canEdit: boolean;
  selection: Selection;
  onAddRoom: () => void;
  onAddSeat: () => void;
  onRotate: () => void;
  onDelete: () => void;
}) {
  if (!canEdit) return null;
  return (
    <div
      role="toolbar"
      aria-label="Floor plan tools"
      className="border-border/60 flex flex-wrap items-center gap-1 border-b px-2 py-1.5"
    >
      <Button variant="ghost" size="sm" onClick={onAddRoom}>
        <SquarePlus aria-hidden="true" className="size-4" />
        Room
      </Button>
      <Button variant="ghost" size="sm" onClick={onAddSeat}>
        <Plus aria-hidden="true" className="size-4" />
        Seat
      </Button>
      <Button variant="ghost" size="sm" onClick={onRotate} disabled={selection?.kind !== "seat"}>
        <RotateCw aria-hidden="true" className="size-4" />
        Rotate
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        disabled={!selection}
        className="text-destructive hover:text-destructive"
      >
        <Trash2 aria-hidden="true" className="size-4" />
        Delete
      </Button>
      <span className="text-muted-foreground ml-auto hidden text-xs sm:inline">
        Drag to move · arrow keys nudge · R rotates
      </span>
    </div>
  );
}

function EditorCanvas({
  plan,
  canEdit,
  invalid,
  selection,
  onSelect,
  onChange,
}: {
  plan: FloorPlanBody;
  canEdit: boolean;
  invalid: Set<number>;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onChange: (mutate: (plan: FloorPlanBody) => FloorPlanBody) => void;
}) {
  const svgRef = React.useRef<SVGSVGElement>(null);
  const dragRef = React.useRef<DragState | null>(null);
  const grid = plan.grid_size || 1;

  function toPlanPoint(event: React.PointerEvent): { x: number; y: number } | null {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm || typeof svg.createSVGPoint !== "function") return null;
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }

  function beginDrag(
    kind: DragState["kind"],
    index: number,
    event: React.PointerEvent<SVGElement>,
  ): void {
    if (!canEdit) return;
    event.stopPropagation();
    const start = toPlanPoint(event);
    onSelect({ kind: kind === "seat" ? "seat" : "zone", index });
    if (!start) return;
    const rect = kind === "seat" ? plan.seats[index] : plan.zones[index];
    if (!rect) return;
    const seatOrigins =
      kind === "zone"
        ? plan.seats
            .map((s, i) => ({ index: i, x: s.x, y: s.y, seat: s }))
            .filter(({ seat }) => seatInZone(seat, plan.zones[index]!))
            .map(({ index: i, x, y }) => ({ index: i, x, y }))
        : [];
    dragRef.current = {
      kind,
      index,
      start,
      origin: { x: rect.x, y: rect.y, w: rect.w, h: rect.h },
      seatOrigins,
    };
    svgRef.current?.setPointerCapture?.(event.pointerId);
  }

  function onPointerMove(event: React.PointerEvent<SVGSVGElement>): void {
    const drag = dragRef.current;
    if (!drag) return;
    const p = toPlanPoint(event);
    if (!p) return;
    const dx = p.x - drag.start.x;
    const dy = p.y - drag.start.y;
    onChange((current) => {
      if (drag.kind === "seat") {
        const seat = current.seats[drag.index];
        if (!seat) return current;
        const x = clamp(snap(drag.origin.x + dx, grid), 0, current.width - seat.w);
        const y = clamp(snap(drag.origin.y + dy, grid), 0, current.height - seat.h);
        return replaceSeat(current, drag.index, { ...seat, x, y });
      }
      const zone = current.zones[drag.index];
      if (!zone) return current;
      if (drag.kind === "resize") {
        const w = clamp(snap(drag.origin.w + dx, grid), DESK_W + 4, current.width - zone.x);
        const h = clamp(snap(drag.origin.h + dy, grid), DESK_H + 4, current.height - zone.y);
        return replaceZone(current, drag.index, { ...zone, w, h });
      }
      const x = clamp(snap(drag.origin.x + dx, grid), 0, current.width - zone.w);
      const y = clamp(snap(drag.origin.y + dy, grid), 0, current.height - zone.h);
      const mx = x - drag.origin.x;
      const my = y - drag.origin.y;
      const seats = current.seats.map((s, i) => {
        const origin = drag.seatOrigins.find((o) => o.index === i);
        if (!origin) return s;
        return {
          ...s,
          x: clamp(origin.x + mx, 0, current.width - s.w),
          y: clamp(origin.y + my, 0, current.height - s.h),
        };
      });
      return { ...replaceZone(current, drag.index, { ...zone, x, y }), seats };
    });
  }

  function endDrag(event: React.PointerEvent<SVGSVGElement>): void {
    if (!dragRef.current) return;
    dragRef.current = null;
    svgRef.current?.releasePointerCapture?.(event.pointerId);
  }

  function onSeatKeyDown(index: number, event: React.KeyboardEvent<SVGGElement>): void {
    if (!canEdit) return;
    const step = event.shiftKey ? grid * 5 : grid;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      onChange((current) => {
        const seat = current.seats[index];
        if (!seat) return current;
        return replaceSeat(current, index, {
          ...seat,
          x: clamp(seat.x + move[0], 0, current.width - seat.w),
          y: clamp(seat.y + move[1], 0, current.height - seat.h),
        });
      });
      return;
    }
    if (event.key === "r" || event.key === "R") {
      event.preventDefault();
      onChange((current) => rotateSeat(current, index));
      return;
    }
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      onChange((current) => removeItem(current, { kind: "seat", index }));
      onSelect(null);
    }
  }

  const selectedZone = selection?.kind === "zone" ? plan.zones[selection.index] : undefined;
  const handle = Math.max(3, Math.min(plan.width, plan.height) / 60);
  const seatLabel = Math.max(2.4, Math.min(DESK_H / 2.4, 4));

  return (
    <div className="bg-muted/30 p-2 sm:p-4">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${plan.width} ${plan.height}`}
        className="h-auto max-h-[70vh] w-full touch-none select-none"
        role="group"
        aria-label={`${plan.name}, ${metres(plan.width)} by ${metres(plan.height)} metres, ${plan.seats.length} seats`}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerDown={() => onSelect(null)}
        data-testid="floor-plan-canvas"
      >
        <FloorBackdrop
          plan={plan}
          selectedZoneIndex={selection?.kind === "zone" ? selection.index : null}
          onZonePointerDown={canEdit ? (i, e) => beginDrag("zone", i, e) : undefined}
        />
        {plan.seats.map((seat, i) => {
          const selected = selection?.kind === "seat" && selection.index === i;
          const bad = invalid.has(i);
          return (
            <g
              key={`${seat.seat_id ?? "new"}-${i}`}
              transform={seatTransform(seat)}
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              aria-label={`Seat ${seat.seat_number}${bad ? ", overlapping" : ""}`}
              data-testid={`floor-seat-${seat.seat_number}`}
              className={cn("outline-none", canEdit && "cursor-grab active:cursor-grabbing")}
              onPointerDown={(e) => beginDrag("seat", i, e)}
              onFocus={() => onSelect({ kind: "seat", index: i })}
              onKeyDown={(e) => onSeatKeyDown(i, e)}
            >
              <rect
                x={seat.x}
                y={seat.y}
                width={seat.w}
                height={seat.h}
                rx={1.2}
                strokeWidth={selected ? 0.9 : 0.5}
                className={cn(
                  "transition-[fill,stroke] duration-150 ease-out",
                  bad
                    ? "fill-destructive/15 stroke-destructive"
                    : selected
                      ? "fill-primary/25 stroke-primary"
                      : "fill-card stroke-foreground/40",
                )}
                strokeDasharray={!seat.seat_id && !bad ? "1.5 1" : undefined}
              />
              <text
                x={seat.x + seat.w / 2}
                y={seat.y + seat.h / 2}
                fontSize={seatLabel}
                textAnchor="middle"
                dominantBaseline="central"
                className="fill-foreground pointer-events-none select-none font-medium tabular-nums"
              >
                {seat.seat_number}
              </text>
            </g>
          );
        })}
        {selectedZone && canEdit ? (
          <rect
            x={selectedZone.x + selectedZone.w - handle}
            y={selectedZone.y + selectedZone.h - handle}
            width={handle}
            height={handle}
            rx={0.6}
            className="fill-primary stroke-background cursor-se-resize"
            strokeWidth={0.4}
            aria-label={`Resize ${selectedZone.label}`}
            onPointerDown={(e) => beginDrag("resize", selection!.index, e)}
          />
        ) : null}
      </svg>
    </div>
  );
}

// ── Inspector ─────────────────────────────────────────────────────────

const NO_ROOM = "__none__";

function Inspector({
  plan,
  selection,
  canEdit,
  onChange,
}: {
  plan: FloorPlanBody;
  selection: Selection;
  canEdit: boolean;
  onChange: (mutate: (plan: FloorPlanBody) => FloorPlanBody) => void;
}) {
  const seat = selection?.kind === "seat" ? plan.seats[selection.index] : undefined;
  const zone = selection?.kind === "zone" ? plan.zones[selection.index] : undefined;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {seat ? `Seat ${seat.seat_number}` : zone ? zone.label : plan.name}
        </CardTitle>
        <CardDescription>
          {seat
            ? seat.seat_id
              ? "Saved seat."
              : "New seat. It is created when you save."
            : zone
              ? `${plan.seats.filter((s) => seatInZone(s, zone)).length} seats in this room.`
              : `${plan.seats.length} seats in ${plan.zones.length} room${plan.zones.length === 1 ? "" : "s"}.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {seat && selection?.kind === "seat" ? (
          <>
            <div className="grid gap-1.5">
              <Label htmlFor="inspector-seat-number">Seat number</Label>
              <Input
                id="inspector-seat-number"
                value={seat.seat_number}
                maxLength={40}
                disabled={!canEdit}
                onChange={(e) =>
                  onChange((p) =>
                    replaceSeat(p, selection.index, {
                      ...seat,
                      seat_number: e.target.value.toUpperCase(),
                    }),
                  )
                }
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="inspector-seat-room">Room</Label>
              <Select
                value={roomValue(plan, seat)}
                disabled={!canEdit}
                onValueChange={(v) => {
                  const target = v === NO_ROOM ? undefined : plan.zones[Number(v)];
                  onChange((p) =>
                    replaceSeat(p, selection.index, {
                      ...seat,
                      zone_id: target?.zone_id ?? null,
                      zone_label: target?.label ?? null,
                    }),
                  );
                }}
              >
                <SelectTrigger id="inspector-seat-room" aria-label="Room">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_ROOM}>No room</SelectItem>
                  {plan.zones.map((z, i) => (
                    <SelectItem key={`${z.zone_id ?? z.label}-${i}`} value={String(i)}>
                      {z.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-muted-foreground text-xs tabular-nums">
              {metres(seat.x)} m from the left, {metres(seat.y)} m from the top · {seat.rotation}°
            </p>
          </>
        ) : zone && selection?.kind === "zone" ? (
          <div className="grid gap-1.5">
            <Label htmlFor="inspector-room-name">Room name</Label>
            <Input
              id="inspector-room-name"
              value={zone.label}
              maxLength={120}
              disabled={!canEdit}
              onChange={(e) =>
                onChange((p) => {
                  const label = e.target.value;
                  const next = replaceZone(p, selection.index, { ...zone, label });
                  return {
                    ...next,
                    seats: next.seats.map((s) =>
                      !zone.zone_id && s.zone_label === zone.label
                        ? { ...s, zone_label: label }
                        : s,
                    ),
                  };
                })
              }
            />
            <p className="text-muted-foreground text-xs tabular-nums">
              {metres(zone.w)} m × {metres(zone.h)} m. Drag the corner handle to resize.
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-1.5">
              <Label htmlFor="inspector-floor-name">Floor name</Label>
              <Input
                id="inspector-floor-name"
                value={plan.name}
                maxLength={120}
                disabled={!canEdit}
                onChange={(e) => onChange((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FloorSizeInput
                id="inspector-floor-width"
                label="Width (m)"
                units={plan.width}
                min={minFloorSize(plan, "x")}
                disabled={!canEdit}
                onCommit={(units) => onChange((p) => ({ ...p, width: units }))}
              />
              <FloorSizeInput
                id="inspector-floor-depth"
                label="Depth (m)"
                units={plan.height}
                min={minFloorSize(plan, "y")}
                disabled={!canEdit}
                onCommit={(units) => onChange((p) => ({ ...p, height: units }))}
              />
            </div>
            <p className="text-muted-foreground text-xs">
              Select a seat or room on the plan to edit it.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FloorSizeInput({
  id,
  label,
  units,
  min,
  disabled,
  onCommit,
}: {
  id: string;
  label: string;
  units: number;
  min: number;
  disabled: boolean;
  onCommit: (units: number) => void;
}) {
  const [raw, setRaw] = React.useState(metres(units));
  React.useEffect(() => setRaw(metres(units)), [units]);
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        inputMode="decimal"
        value={raw}
        disabled={disabled}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => {
          const n = Number.parseFloat(raw);
          if (!Number.isFinite(n)) {
            setRaw(metres(units));
            return;
          }
          onCommit(clamp(Math.round(n * 10), Math.max(min, 20), 5000));
        }}
      />
    </div>
  );
}

// ── Pure plan edits ───────────────────────────────────────────────────

function replaceSeat(plan: FloorPlanBody, index: number, seat: FloorSeatPlacement): FloorPlanBody {
  return { ...plan, seats: plan.seats.map((s, i) => (i === index ? seat : s)) };
}

function replaceZone(plan: FloorPlanBody, index: number, zone: FloorZoneShape): FloorPlanBody {
  return { ...plan, zones: plan.zones.map((z, i) => (i === index ? zone : z)) };
}

function seatInZone(seat: FloorSeatPlacement, zone: FloorZoneShape): boolean {
  if (zone.zone_id && seat.zone_id) return seat.zone_id === zone.zone_id;
  if (!zone.zone_id && seat.zone_label) return seat.zone_label === zone.label;
  const cx = seat.x + seat.w / 2;
  const cy = seat.y + seat.h / 2;
  return cx >= zone.x && cx <= zone.x + zone.w && cy >= zone.y && cy <= zone.y + zone.h;
}

function roomValue(plan: FloorPlanBody, seat: FloorSeatPlacement): string {
  const index = plan.zones.findIndex((z) =>
    z.zone_id ? z.zone_id === seat.zone_id : !seat.zone_id && z.label === seat.zone_label,
  );
  return index >= 0 ? String(index) : NO_ROOM;
}

function selectedZoneIndex(plan: FloorPlanBody, selection: Selection): number | null {
  if (selection?.kind === "zone") return selection.index;
  if (selection?.kind === "seat") {
    const seat = plan.seats[selection.index];
    if (!seat) return null;
    const index = plan.zones.findIndex((z) => seatInZone(seat, z));
    return index >= 0 ? index : null;
  }
  return plan.zones.length > 0 ? 0 : null;
}

function addRoom(plan: FloorPlanBody): FloorPlanBody {
  const w = Math.min(80, plan.width);
  const h = Math.min(50, plan.height);
  const label = uniqueRoomLabel(plan);
  return {
    ...plan,
    zones: [
      ...plan.zones,
      {
        zone_id: null,
        label,
        color_role: "primary",
        x: clamp(snap((plan.width - w) / 2, plan.grid_size || 1), 0, plan.width - w),
        y: clamp(snap((plan.height - h) / 2, plan.grid_size || 1), 0, plan.height - h),
        w,
        h,
      },
    ],
  };
}

function uniqueRoomLabel(plan: FloorPlanBody): string {
  const taken = new Set(plan.zones.map((z) => z.label));
  for (let n = plan.zones.length + 1; n < 1000; n += 1) {
    const label = `Room ${n}`;
    if (!taken.has(label)) return label;
  }
  return `Room ${Date.now()}`;
}

function addSeat(plan: FloorPlanBody, zoneIndex: number | null): FloorPlanBody {
  const zone = zoneIndex === null ? undefined : plan.zones[zoneIndex];
  const area = zone ?? { x: 0, y: 0, w: plan.width, h: plan.height };
  const spot = findFreeSpot(plan, area);
  const seat: FloorSeatPlacement = {
    seat_id: null,
    zone_id: zone?.zone_id ?? null,
    zone_label: zone?.label ?? null,
    seat_number: nextSeatNumber(plan, zone?.label ?? "Seat"),
    seat_type: "standard",
    rotation: 0,
    x: spot.x,
    y: spot.y,
    w: DESK_W,
    h: DESK_H,
  };
  return { ...plan, seats: [...plan.seats, seat] };
}

function rotateSeat(plan: FloorPlanBody, index: number): FloorPlanBody {
  const seat = plan.seats[index];
  if (!seat) return plan;
  return replaceSeat(plan, index, { ...seat, rotation: (seat.rotation + 90) % 360 });
}

function removeItem(plan: FloorPlanBody, selection: NonNullable<Selection>): FloorPlanBody {
  if (selection.kind === "seat") {
    return { ...plan, seats: plan.seats.filter((_, i) => i !== selection.index) };
  }
  return { ...plan, zones: plan.zones.filter((_, i) => i !== selection.index) };
}

function minFloorSize(plan: FloorPlanBody, axis: "x" | "y"): number {
  const extents = [
    ...plan.seats.map((s) => (axis === "x" ? s.x + s.w : s.y + s.h)),
    ...plan.zones.map((z) => (axis === "x" ? z.x + z.w : z.y + z.h)),
    ...plan.features.map((f) => (axis === "x" ? f.x + f.w : f.y + f.h)),
  ];
  return Math.ceil(Math.max(0, ...extents));
}
